(ns practitest.frontend.components2.dashboard.charts.line.render-lines
  (:require
   [practitest.frontend.components2.dashboard.charts.line.helper :as helper]
   [practitest.frontend.components2.dashboard.charts.render-helpers :as rh]
   ["d3" :as d3]))

(defn- make-line-generator [chart scales]
  (let [{:keys [x-scale y-scale]} scales
        {:config/keys [define-fn]} chart
        gen (-> d3
                (.line)
                (.x (fn [d] (x-scale (aget d "x-data"))))
                (.y (fn [d] (y-scale (aget d "y-data"))))
                (.defined (fn [d] (define-fn d))))]
    gen))

(defn- prepare-js-data [line]
  (clj->js (mapv (fn [d] (assoc d :x-data (:parsed-x d))) (:line-data line))))

(defn- animate-trace-in!
  "Apply stroke-dashoffset trace animation to a path selection.
   Measures the path length, hides it via dashoffset, then transitions
   dashoffset to 0 so the line traces from start to end.
   Sets data-tracing attribute to track animation state."
  [path-sel duration ease-fn]
  (let [total-length (.getTotalLength (.node path-sel))]
    (if (pos? total-length)
      (-> path-sel
          (.attr "data-tracing" "true")
          (.attr "stroke-dasharray" total-length)
          (.attr "stroke-dashoffset" total-length)
          (.transition)
          (.duration duration)
          (.ease ease-fn)
          (.attr "stroke-dashoffset" 0)
          (.on "end" (fn []
                       (-> path-sel
                           (.attr "stroke-dasharray" "none")
                           (.attr "data-tracing" nil)))))
      (-> path-sel
          (.attr "stroke-dasharray" "none")
          (.attr "stroke-dashoffset" 0)))))

(defn render-lines! [svg chart lines scales hidden-lines]
  (let [lines-group (rh/ensure-group! svg "lines-group")
        animation (or (:config/animation chart) {})
        generator (make-line-generator chart scales)
        hidden-set (or hidden-lines #{})

        {:keys [line-draw-duration line-draw-ease
                resize-duration resize-ease toggle-out-duration]} animation
        draw-duration (or line-draw-duration 1200)
        draw-ease (helper/resolve-ease line-draw-ease)
        r-duration (or resize-duration 350)
        r-ease (helper/resolve-ease resize-ease)

        key-fn (fn [d] (aget d "line-name"))
        all-js-lines (clj->js
                      (mapv (fn [line]
                              #js {:line-name (:line-name line)
                                   :line-color (:line-color line)
                                   :line-width (:line-width line)
                                   :line-data (prepare-js-data line)
                                   :hidden (contains? hidden-set (:line-name line))})
                            lines))

        paths (-> lines-group
                  (.selectAll "path.line")
                  (.data all-js-lines key-fn))]

    ;; --- Enter selection (first render / structural data change) ---
    (-> (.enter paths)
        (.append "path")
        (.attr "class" "line")
        (.attr "fill" "none")
        (.attr "stroke" (fn [d] (aget d "line-color")))
        (.attr "stroke-width" (fn [d] (aget d "line-width")))
        (.attr "d" (fn [d] (generator (aget d "line-data"))))
        (.attr "opacity" (fn [d] (if (aget d "hidden") 0 1)))
        (.each (fn [d]
                 (this-as this
                   (when-not (aget d "hidden")
                     (animate-trace-in! (.select d3 this) draw-duration draw-ease))))))

    ;; --- Update selection (per-element: resize, toggle-in, toggle-out) ---
    (.each paths
      (fn [d]
        (this-as this
          (let [sel (.select d3 this)
                hidden? (aget d "hidden")
                current-opacity (js/parseFloat (.attr sel "opacity"))]
            (cond
              ;; Toggle-in: was hidden (opacity ~0), now visible
              (and (not hidden?) (<= current-opacity 0))
              (do
                (.interrupt sel)
                (.attr sel "d" (generator (aget d "line-data")))
                (.attr sel "opacity" 1)
                (animate-trace-in! sel draw-duration draw-ease))

              ;; Toggle-out: visible -> hidden
              hidden?
              (do
                (.interrupt sel)
                (-> sel
                    (.transition)
                    (.duration (or toggle-out-duration 300))
                    (.attr "opacity" 0)))

              ;; Normal update (resize / data change)
              :else
              (let [tracing? (= "true" (.attr sel "data-tracing"))
                    new-d (generator (aget d "line-data"))
                    d-changed? (not= new-d (.attr sel "d"))]
                (cond
                  ;; Tracing + data changed (resize during animation) -> restart trace
                  (and tracing? d-changed?)
                  (do
                    (.interrupt sel)
                    (.attr sel "d" new-d)
                    (animate-trace-in! sel draw-duration draw-ease))

                  ;; Tracing + data unchanged -> let trace continue
                  tracing?
                  nil

                  ;; Not tracing + data changed -> normal resize transition
                  d-changed?
                  (do
                    (.interrupt sel)
                    (.attr sel "stroke-dasharray" "none")
                    (.attr sel "stroke-dashoffset" 0)
                    (-> sel
                        (.transition)
                        (.duration r-duration)
                        (.ease r-ease)
                        (.attr "d" new-d))))))))))

    ;; --- Exit selection ---
    (-> (.exit paths)
        (.transition)
        (.duration (or toggle-out-duration 300))
        (.attr "opacity" 0)
        (.remove))))
